import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateRangeSelector from '../../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../../components/FilterSelectorWithApp';
import RouletteRevenueAndTicketChart from '../../../../components/pointhome/roulette/revenue/RouletteRevenueAndTicketChart';
import type { RevenueAndTicketDatum } from '../../../../components/pointhome/roulette/revenue/RouletteRevenueAndTicketChart';
import RouletteRevenueTable from '../../../../components/pointhome/roulette/revenue/RouletteRevenueTable';
import type { RouletteRevenueRow } from '../../../../components/pointhome/roulette/revenue/RouletteRevenueTable';
import RouletteRevenueContentSalesChart from '../../../../components/pointhome/roulette/revenue/RouletteRevenueContentSalesChart';
import RouletteRevenueUserTypeTicketChart from '../../../../components/pointhome/roulette/revenue/RouletteRevenueUserTypeTicketChart';
import apiClient from '../../../../utils/axiosClient';

dayjs.locale('ko');

export type RouletteTotalRevenueItem = {
  totalRevenue: number;
  prevTotalRevenue: number;
  totalExpense: number;
  prevTotalExpense: number;
  totalTicket: { newUserTicket: number; remainingUserTicket: number };
  prevTotalTicket: { newUserTicket: number; remainingUserTicket: number };
  dailyRevenue: Array<{ date: string; revenue: number }>;
  dailyTicket?: Array<{ date: string; newUserTicket: number; remainingUserTicket: number }>;
  contentRevenue: Array<{ contentType: string; revenue: number }>;
  contentDailyRevenue: Array<{ date: string; contentType: string; revenue: number }>;
  /** 콘텐츠별 일별 티켓 (테이블 컬럼별 기존/신규 표시용) */
  contentDailyTicket?: Array<{
    date: string;
    contentType: string;
    newUserTicket: number;
    remainingUserTicket: number;
  }>;
};

/** 콘텐츠별 API (revenue/content) - dailyRevenue, dailyTicket 포함 */
type RouletteContentRevenueItem = {
  totalRevenue: number;
  prevTotalRevenue: number;
  totalTicket: { newUserTicket: number; remainingUserTicket: number };
  prevTotalTicket: { newUserTicket: number; remainingUserTicket: number };
  dailyRevenue: Array<{ date: string; revenue: number }>;
  dailyTicket?: Array<{ date: string; newUserTicket: number; remainingUserTicket: number }>;
};

const REVENUE_CONTENT_TABS = [
  '전체',
  '튜브 낚시',
  '뜰채 낚시',
  '통발 수리',
  '통발 건지기',
  '낚시운세',
  '랜덤 광고',
  '광고보기',
  '영상 광고',
  '홈 방문하기',
  '룰렛 더 모으기',
  '출석체크',
] as const;

export type RevenueContentFilterValue = (typeof REVENUE_CONTENT_TABS)[number];

const formatCurrency = (value: number) => `₩${Math.round(value).toLocaleString('ko-KR')}`;

const CONTENT_TYPE_LABEL: Record<string, string> = {
  tube: '튜브 낚시',
  scoop: '뜰채 낚시',
  homevisit: '홈 방문하기',
  shortad: '랜덤 광고',
  adticket: '광고보기',
  longad: '영상 광고',
  repair: '통발 수리',
  pullup: '통발 건지기',
  fortune: '낚시운세',
  rc_more_collect: '룰렛 더 모으기',
  attendance: '출석체크',
  coupang_seller: '쿠팡 셀러',
};

/** 탭 인덱스(1~9) -> content API의 content_type */
const CONTENT_TAB_INDEX_TO_TYPE: Record<number, string> = {
  1: 'tube',
  2: 'scoop',
  3: 'repair',
  4: 'pullup',
  5: 'fortune',
  6: 'shortad',
  7: 'adticket',
  8: 'longad',
  9: 'homevisit',
  10: 'rc_more_collect',
  11: 'attendance',
};

function formatDelta(value: number, prev: number): { text: string; isUp: boolean } {
  const diff = value - prev;
  const sign = diff >= 0 ? '+' : '';
  if (prev === 0) {
    return {
      text: `${value >= 0 ? '+' : ''}${value.toLocaleString('ko-KR')} (—%)`,
      isUp: value >= 0,
    };
  }
  const pct = Math.round((diff / prev) * 100);
  return { text: `${sign}${diff.toLocaleString('ko-KR')} (${sign}${pct}%)`, isUp: diff >= 0 };
}

/** 일별 매출 + 일별 티켓을 날짜 기준으로 합쳐서 차트용 데이터 생성 */
function buildRevenueAndTicketChartData(
  dailyRevenue: RouletteTotalRevenueItem['dailyRevenue'],
  dailyTicket: RouletteTotalRevenueItem['dailyTicket'],
): RevenueAndTicketDatum[] {
  const ticketByDate = new Map<string, { newUserTicket: number; remainingUserTicket: number }>();
  (dailyTicket ?? []).forEach((row) => {
    ticketByDate.set(row.date, {
      newUserTicket: row.newUserTicket ?? 0,
      remainingUserTicket: row.remainingUserTicket ?? 0,
    });
  });
  const revenueByDate = new Map<string, number>();
  dailyRevenue.forEach((row) => revenueByDate.set(row.date, row.revenue ?? 0));
  const allDates = Array.from(
    new Set(Array.from(revenueByDate.keys()).concat(Array.from(ticketByDate.keys()))),
  ).sort();
  return allDates.map((date) => {
    const ticket = ticketByDate.get(date) ?? { newUserTicket: 0, remainingUserTicket: 0 };
    const revenue = revenueByDate.get(date) ?? 0;
    const ticketTotal = ticket.newUserTicket + ticket.remainingUserTicket;
    return {
      label: dayjs(date).format('MM/DD'),
      date: date,
      revenue,
      ticketTotal,
      remainingUserTicket: ticket.remainingUserTicket,
      newUserTicket: ticket.newUserTicket,
    };
  });
}

function buildRevenueTableRows(
  contentDailyRevenue: RouletteTotalRevenueItem['contentDailyRevenue'],
  contentDailyTicket?: RouletteTotalRevenueItem['contentDailyTicket'],
  dailyTicket?: RouletteTotalRevenueItem['dailyTicket'],
): RouletteRevenueRow[] {
  const byDate: Record<
    string,
    {
      total: number;
      tube: number;
      scoop: number;
      homeVisit: number;
      shortAd: number;
      adTicket: number;
      videoAd: number;
      repair: number;
      pullup: number;
      fortune: number;
      rcMoreCollect: number;
      attendance: number;
      coupangSeller: number;
    }
  > = {};
  for (const row of contentDailyRevenue) {
    const d = row.date;
    if (!byDate[d]) {
      byDate[d] = {
        total: 0,
        tube: 0,
        scoop: 0,
        homeVisit: 0,
        shortAd: 0,
        adTicket: 0,
        videoAd: 0,
        repair: 0,
        pullup: 0,
        fortune: 0,
        rcMoreCollect: 0,
        attendance: 0,
        coupangSeller: 0,
      };
    }
    const r = byDate[d];
    const rev = row.revenue ?? 0;
    r.total += rev;
    if (row.contentType === 'tube') r.tube += rev;
    else if (row.contentType === 'scoop') r.scoop += rev;
    else if (row.contentType === 'homevisit') r.homeVisit += rev;
    else if (row.contentType === 'shortad') r.shortAd += rev;
    else if (row.contentType === 'adticket') r.adTicket += rev;
    else if (row.contentType === 'longad') r.videoAd += rev;
    else if (row.contentType === 'repair') r.repair += rev;
    else if (row.contentType === 'pullup') r.pullup += rev;
    else if (row.contentType === 'fortune') r.fortune += rev;
    else if (row.contentType === 'rc_more_collect') r.rcMoreCollect += rev;
    else if (row.contentType === 'attendance') r.attendance += rev;
    else if (row.contentType === 'coupang_seller') r.coupangSeller += rev;
  }
  const ticketByDateContent = new Map<
    string,
    Map<string, { remainingUserTicket: number; newUserTicket: number }>
  >();
  (contentDailyTicket ?? []).forEach((row) => {
    const d = String(row.date ?? '');
    const ct = String(row.contentType ?? '');
    let byContent = ticketByDateContent.get(d);
    if (!byContent) {
      byContent = new Map();
      ticketByDateContent.set(d, byContent);
    }
    byContent.set(ct, {
      remainingUserTicket: row.remainingUserTicket ?? 0,
      newUserTicket: row.newUserTicket ?? 0,
    });
  });
  const dailyTicketByDate = new Map<
    string,
    { remainingUserTicket: number; newUserTicket: number }
  >();
  (dailyTicket ?? []).forEach((row) => {
    dailyTicketByDate.set(row.date, {
      remainingUserTicket: row.remainingUserTicket ?? 0,
      newUserTicket: row.newUserTicket ?? 0,
    });
  });
  return Object.entries(byDate)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, v], i) => {
      const byContent = ticketByDateContent.get(date);
      const getTicket = (contentType: string) => byContent?.get(contentType);
      const tube = getTicket('tube');
      const scoopRaw = getTicket('scoop');
      const scoopBonus = getTicket('scoop_bonus');
      const scoop =
        scoopRaw && scoopBonus
          ? {
              remainingUserTicket: scoopRaw.remainingUserTicket + scoopBonus.remainingUserTicket,
              newUserTicket: scoopRaw.newUserTicket + scoopBonus.newUserTicket,
            }
          : (scoopRaw ?? scoopBonus);
      const homeVisit = getTicket('homevisit');
      const shortAd = getTicket('shortad');
      const adTicket = getTicket('adticket');
      const longad = getTicket('longad');
      const repair = getTicket('repair');
      const pullup = getTicket('pullup');
      const fortune = getTicket('fortune');
      const rcMoreCollect = getTicket('rc_more_collect');
      const attendance = getTicket('attendance');
      // ponytail: 콘텐츠별 재합산 대신 백엔드가 이미 전체 합산해둔 값을 그대로 사용 (새 content_type 추가돼도 누락 없음)
      const ticketTotal = dailyTicketByDate.get(date);
      return {
        id: i + 1,
        date,
        total: v.total,
        tubeFishing: v.tube,
        scoopFishing: v.scoop,
        homeVisit: v.homeVisit,
        shortAd: v.shortAd,
        adTicket: v.adTicket,
        videoAd: v.videoAd,
        repair: v.repair,
        pullup: v.pullup,
        fortune: v.fortune,
        rcMoreCollect: v.rcMoreCollect,
        attendance: v.attendance,
        coupangSeller: v.coupangSeller,
        ticketTotal,
        ticketTube: tube,
        ticketScoop: scoop,
        ticketHomeVisit: homeVisit,
        ticketShortAd: shortAd,
        ticketAdTicket: adTicket,
        ticketVideoAd: longad,
        ticketRepair: repair,
        ticketPullup: pullup,
        ticketFortune: fortune,
        ticketRcMoreCollect: rcMoreCollect,
        ticketAttendance: attendance,
      };
    });
}

export default function RouletteRevenue() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [contentTab, setContentTab] = useState(0);
  const [revenueData, setRevenueData] = useState<RouletteTotalRevenueItem | null>(null);
  const [contentTicketData, setContentTicketData] = useState<RouletteContentRevenueItem | null>(
    null,
  );
  const [contentTicketLoading, setContentTicketLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      })
      .catch(() => undefined);
  }, []);

  const fetchRevenue = useCallback(() => {
    if (!startDate || !endDate) return;
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : (selectedApp ?? 'all');
    setLoading(true);
    setError(null);
    apiClient
      .get<RouletteTotalRevenueItem[]>('/api/v1/stat/pointhome/roulette/revenue/total', {
        params: { start_date: start, end_date: end, app },
      })
      .then((response) => {
        const list = response.data;
        const raw = Array.isArray(list) && list.length > 0 ? list[0] : null;
        if (!raw) {
          setRevenueData(null);
          return;
        }
        const dailyTicketRaw = raw.dailyTicket ?? (raw as Record<string, unknown>).daily_ticket;
        const dailyTicket = Array.isArray(dailyTicketRaw)
          ? dailyTicketRaw.map((row: Record<string, unknown>) => ({
              date: String(row.date ?? ''),
              newUserTicket: Number(row.newUserTicket ?? row.new_user_ticket ?? 0),
              remainingUserTicket: Number(
                row.remainingUserTicket ?? row.remaining_user_ticket ?? 0,
              ),
            }))
          : undefined;
        const contentDailyTicketRaw =
          raw.contentDailyTicket ?? (raw as Record<string, unknown>).content_daily_ticket;
        const contentDailyTicket = Array.isArray(contentDailyTicketRaw)
          ? contentDailyTicketRaw.map((row: Record<string, unknown>) => ({
              date: String(row.date ?? ''),
              contentType: String(row.contentType ?? row.content_type ?? ''),
              newUserTicket: Number(row.newUserTicket ?? row.new_user_ticket ?? 0),
              remainingUserTicket: Number(
                row.remainingUserTicket ?? row.remaining_user_ticket ?? 0,
              ),
            }))
          : undefined;
        setRevenueData({ ...raw, dailyTicket, contentDailyTicket });
      })
      .catch((err) => {
        setRevenueData(null);
        setError(err?.response?.data?.detail ?? err?.message ?? '조회 실패');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp]);

  const fetchContentRevenue = useCallback(() => {
    if (!startDate || !endDate || contentTab < 1) return;
    const contentType = CONTENT_TAB_INDEX_TO_TYPE[contentTab];
    if (!contentType) return;
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : (selectedApp ?? 'all');
    setContentTicketLoading(true);
    setContentTicketData(null);
    apiClient
      .get<RouletteContentRevenueItem[]>('/api/v1/stat/pointhome/roulette/revenue/content', {
        params: { start_date: start, end_date: end, app, content_type: contentType },
      })
      .then((response) => {
        const list = response.data;
        const raw = Array.isArray(list) && list.length > 0 ? list[0] : null;
        if (!raw) {
          setContentTicketData(null);
          return;
        }
        const dailyRevenue = Array.isArray(raw.dailyRevenue)
          ? raw.dailyRevenue.map((row: { date?: string; revenue?: number }) => ({
              date: String(row.date ?? ''),
              revenue: Number(row.revenue ?? 0),
            }))
          : [];
        const dailyTicketRaw = raw.dailyTicket ?? (raw as Record<string, unknown>).daily_ticket;
        const dailyTicket = Array.isArray(dailyTicketRaw)
          ? dailyTicketRaw.map((row: Record<string, unknown>) => ({
              date: String(row.date ?? ''),
              newUserTicket: Number(row.newUserTicket ?? row.new_user_ticket ?? 0),
              remainingUserTicket: Number(
                row.remainingUserTicket ?? row.remaining_user_ticket ?? 0,
              ),
            }))
          : undefined;
        setContentTicketData({ ...raw, dailyRevenue, dailyTicket });
      })
      .catch(() => setContentTicketData(null))
      .finally(() => setContentTicketLoading(false));
  }, [startDate, endDate, selectedApp, contentTab]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  useEffect(() => {
    if (contentTab === 0) {
      setContentTicketData(null);
      return;
    }
    fetchContentRevenue();
  }, [contentTab, fetchContentRevenue]);

  return (
    <Box
      sx={{
        opacity: loading || (contentTab >= 1 && contentTicketLoading) ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* 상단 필터: 기간, 앱 (박스 밖) */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 180 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
          />
        </Box>
      </Box>

      {/* 박스: 앱 리스트 → 구분선 → 탭 */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        <Tabs
          value={contentTab}
          onChange={(_, newValue: number) => setContentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'normal',
              px: 2,
              py: 1,
              borderRadius: '8px',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 68, 210, 0.1)',
                color: '#0044D2',
                fontWeight: 'bold',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab label="전체" />
          <Tab label="튜브 낚시" />
          <Tab label="뜰채 낚시" />
          <Tab label="통발 수리" />
          <Tab label="통발 건지기" />
          <Tab label="낚시운세" />
          <Tab label="랜덤 광고" />
          <Tab label="광고보기" />
          <Tab label="영상 광고" />
          <Tab label="홈 방문하기" />
          <Tab label="룰렛 더 모으기" />
          <Tab label="출석체크" />
        </Tabs>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && revenueData && contentTab >= 1 && contentTicketLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && revenueData && !(contentTab >= 1 && contentTicketLoading) && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  contentTab === 0
                    ? { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }
                    : { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                mt: 3,
              }}
            >
              {contentTab === 0
                ? (() => {
                    const totalTicketCount =
                      (revenueData.totalTicket?.newUserTicket ?? 0) +
                      (revenueData.totalTicket?.remainingUserTicket ?? 0);
                    const prevTotalTicketCount =
                      (revenueData.prevTotalTicket?.newUserTicket ?? 0) +
                      (revenueData.prevTotalTicket?.remainingUserTicket ?? 0);
                    const ticketDelta = formatDelta(totalTicketCount, prevTotalTicketCount);
                    const revenueDelta = formatDelta(
                      revenueData.totalRevenue,
                      revenueData.prevTotalRevenue,
                    );
                    const expenseDelta = formatDelta(
                      revenueData.totalExpense,
                      revenueData.prevTotalExpense,
                    );
                    const revPerTicket =
                      totalTicketCount > 0 ? revenueData.totalRevenue / totalTicketCount : 0;
                    const prevRevPerTicket =
                      prevTotalTicketCount > 0
                        ? revenueData.prevTotalRevenue / prevTotalTicketCount
                        : 0;
                    const costPerTicket =
                      totalTicketCount > 0 ? revenueData.totalExpense / totalTicketCount : 0;
                    const prevCostPerTicket =
                      prevTotalTicketCount > 0
                        ? revenueData.prevTotalExpense / prevTotalTicketCount
                        : 0;
                    return (
                      <>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              전체 매출
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#0044D2">
                            {formatCurrency(revenueData.totalRevenue)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비{' '}
                            <Box
                              component="span"
                              sx={{
                                color: revenueDelta.isUp ? '#D32F2F' : '#1976D2',
                                fontWeight: 600,
                              }}
                            >
                              {revenueDelta.text}
                            </Box>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              전체 비용
                            </Typography>
                            <Tooltip
                              title={
                                <>
                                  조회된 기간 동안 룰렛에서 사용자에게 지급된 <br></br>총 캐시를
                                  원으로 전환한 값입니다.
                                </>
                              }
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                sx={{ p: 0, color: '#64748b' }}
                                aria-label="전체 비용 설명"
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#374151">
                            {formatCurrency(revenueData.totalExpense)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비{' '}
                            <Box
                              component="span"
                              sx={{
                                color: expenseDelta.isUp ? '#D32F2F' : '#1976D2',
                                fontWeight: 600,
                              }}
                            >
                              {expenseDelta.text}
                            </Box>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              전체 티켓 발급 수
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#0044D2">
                            {totalTicketCount.toLocaleString('ko-KR')} 장
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비{' '}
                            <Box
                              component="span"
                              sx={{
                                color: ticketDelta.isUp ? '#D32F2F' : '#1976D2',
                                fontWeight: 600,
                              }}
                            >
                              {ticketDelta.text.replace(/^([-+]?\d[\d,]*)\s*/, '$1장 ')}
                            </Box>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              티켓 장당 매출/단가
                            </Typography>
                            <Tooltip
                              title={
                                <>
                                  · 티켓 장당 매출 = 전체 매출 / 전체 티켓 발급 수
                                  <br />· 티켓 장당 단가 = 전체 비용 / 사용 티켓 수
                                </>
                              }
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                sx={{ p: 0, color: '#64748b' }}
                                aria-label="티켓 장당 매출/단가 설명"
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#374151">
                            {revPerTicket.toFixed(1)}원 / {costPerTicket.toFixed(1)}원
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비 {revPerTicket - prevRevPerTicket >= 0 ? '+' : ''}
                            {(revPerTicket - prevRevPerTicket).toFixed(1)}원 /{' '}
                            {costPerTicket - prevCostPerTicket >= 0 ? '+' : ''}
                            {(costPerTicket - prevCostPerTicket).toFixed(1)}원
                          </Typography>
                        </Box>
                      </>
                    );
                  })()
                : (() => {
                    const contentRevenue = contentTicketData?.totalRevenue ?? 0;
                    const prevContentRevenue = contentTicketData?.prevTotalRevenue ?? 0;
                    const contentTicketCount =
                      (contentTicketData?.totalTicket?.newUserTicket ?? 0) +
                      (contentTicketData?.totalTicket?.remainingUserTicket ?? 0);
                    const prevContentTicketCount =
                      (contentTicketData?.prevTotalTicket?.newUserTicket ?? 0) +
                      (contentTicketData?.prevTotalTicket?.remainingUserTicket ?? 0);
                    const contentRevenueDelta = formatDelta(contentRevenue, prevContentRevenue);
                    const contentTicketDelta = formatDelta(
                      contentTicketCount,
                      prevContentTicketCount,
                    );
                    return (
                      <>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              전체 매출
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#0044D2">
                            {formatCurrency(contentRevenue)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비{' '}
                            <Box
                              component="span"
                              sx={{
                                color: contentRevenueDelta.isUp ? '#D32F2F' : '#1976D2',
                                fontWeight: 600,
                              }}
                            >
                              {contentRevenueDelta.text}
                            </Box>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            minHeight: 100,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">
                              전체 티켓 발급 수
                            </Typography>
                          </Box>
                          <Typography variant="h5" fontWeight="bold" color="#0044D2">
                            {contentTicketCount.toLocaleString('ko-KR')} 장
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: '#64748b' }}
                          >
                            전월 동기간 대비{' '}
                            <Box
                              component="span"
                              sx={{
                                color: contentTicketDelta.isUp ? '#D32F2F' : '#1976D2',
                                fontWeight: 600,
                              }}
                            >
                              {contentTicketDelta.text.replace(/^([-+]?\d[\d,]*)\s*/, '$1장 ')}
                            </Box>
                          </Typography>
                        </Box>
                      </>
                    );
                  })()}
            </Box>

            <Box sx={{ mt: 3 }}>
              {contentTab === 0 ? (
                <RouletteRevenueAndTicketChart
                  data={buildRevenueAndTicketChartData(
                    revenueData.dailyRevenue,
                    revenueData.dailyTicket,
                  )}
                />
              ) : contentTicketData ? (
                <RouletteRevenueAndTicketChart
                  data={buildRevenueAndTicketChartData(
                    contentTicketData.dailyRevenue ?? [],
                    contentTicketData.dailyTicket,
                  )}
                />
              ) : (
                <RouletteRevenueAndTicketChart data={[]} />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              {contentTab === 0 && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RouletteRevenueContentSalesChart
                    data={revenueData.contentRevenue.map(({ contentType, revenue }) => {
                      const total = revenueData.contentRevenue.reduce((s, r) => s + r.revenue, 0);
                      const pct = total > 0 ? (revenue / total) * 100 : 0;
                      return {
                        name: CONTENT_TYPE_LABEL[contentType] ?? contentType,
                        value: pct,
                        percent: pct,
                        amount: revenue,
                      };
                    })}
                  />
                </Box>
              )}
              <Box
                sx={{
                  flex: contentTab >= 1 ? '0 1 50%' : 1,
                  minWidth: 0,
                  maxWidth: contentTab >= 1 ? '50%' : undefined,
                }}
              >
                {contentTab === 0 ? (
                  <RouletteRevenueUserTypeTicketChart
                    data={[
                      {
                        name: '기존 사용자',
                        value: revenueData.totalTicket?.remainingUserTicket ?? 0,
                        color: '#1E88E5',
                      },
                      {
                        name: '신규 사용자',
                        value: revenueData.totalTicket?.newUserTicket ?? 0,
                        color: '#FF9800',
                      },
                    ]}
                  />
                ) : (
                  <RouletteRevenueUserTypeTicketChart
                    data={[
                      {
                        name: '기존 사용자',
                        value: contentTicketData?.totalTicket?.remainingUserTicket ?? 0,
                        color: '#1E88E5',
                      },
                      {
                        name: '신규 사용자',
                        value: contentTicketData?.totalTicket?.newUserTicket ?? 0,
                        color: '#FF9800',
                      },
                    ]}
                  />
                )}
              </Box>
            </Box>

            {contentTab === 0 && (
              <Box sx={{ mt: 3 }}>
                <RouletteRevenueTable
                  rows={buildRevenueTableRows(
                    revenueData.contentDailyRevenue,
                    revenueData.contentDailyTicket,
                    revenueData.dailyTicket,
                  )}
                  startDate={startDate}
                  endDate={endDate}
                />
              </Box>
            )}
          </>
        )}

        {!loading && !revenueData && !error && (
          <Typography color="text.secondary" sx={{ mt: 3 }}>
            기간과 앱을 선택한 뒤 조회됩니다.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
